import * as THREE from 'three';
import { TICK_HZ } from '@somnhold/content';
import { pensKept, trailWashed, type BattleState } from '@somnhold/sim';
import { simToWorld } from '@somnhold/render';
import { pinMark, remeasureMark } from './mark-pin.ts';
import { t } from '../i18n.ts';
import { h, setStyle } from '../ui/dom.ts';

/** A stone boar, side on. */
const BOAR = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2.2 9.2c0-2.6 2.2-4.4 5.3-4.4 2.3 0 3.9.8 4.8 2.1l1.6-.5-.6 1.9c.3.5.5 1 .5 1.6 0 1.5-1.2 2.3-3 2.3H5.1c-1.8 0-2.9-1.2-2.9-3z" fill="currentColor"/><path d="M4.4 12.2v1.6M6.6 12.2v1.6M9.6 12.2v1.6M11.6 12.2v1.6" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>';
/** A wolf's paw print. */
const PAW = '<svg viewBox="0 0 16 16" aria-hidden="true"><ellipse cx="8" cy="10.6" rx="3.2" ry="2.7" fill="currentColor"/><circle cx="4.1" cy="6.6" r="1.4" fill="currentColor"/><circle cx="6.6" cy="4.4" r="1.4" fill="currentColor"/><circle cx="9.4" cy="4.4" r="1.4" fill="currentColor"/><circle cx="11.9" cy="6.6" r="1.4" fill="currentColor"/></svg>';
/** Rain falling slant. */
const RAIN = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4 3l-2 5M8 2l-2.4 6M12 3l-2 5M6 9l-1.6 4M10 9l-1.6 4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>';
/** A wolf's head raised, howling. */
const HOWL = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 13.5l1.8-5.2L3.6 4l2.6 1.8L9.4 2l.8 3.4 3.3 1.4-2.7 1.4L9 13.5z" fill="currentColor"/><path d="M12.8 2.6c.9.7 1.4 1.6 1.4 2.6M11.9 1.2c1.5.9 2.4 2.4 2.5 4" stroke="currentColor" stroke-width="1" fill="none" stroke-linecap="round"/></svg>';
/** The pack's clues (jump, drink) and the Wolf's (stones, pit), as the board marks them. */
const JUMP = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 11.5c1.5-6 9.5-6 11 0" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linecap="round"/><path d="M1.5 13.5h13" stroke="currentColor" stroke-width="1.2" stroke-dasharray="2 1.5"/></svg>';
const DRINK = '<svg viewBox="0 0 16 16" aria-hidden="true"><ellipse cx="8" cy="9" rx="3" ry="2" fill="currentColor"/><circle cx="8" cy="9" r="5.6" stroke="currentColor" stroke-width="1.2" stroke-dasharray="1.6 1.4" fill="none"/></svg>';
const STONES = '<svg viewBox="0 0 16 16" aria-hidden="true"><ellipse cx="3.6" cy="9" rx="2.2" ry="1.4" fill="currentColor"/><ellipse cx="8" cy="8" rx="2.2" ry="1.4" fill="currentColor"/><ellipse cx="12.4" cy="9" rx="2.2" ry="1.4" fill="currentColor"/><path d="M1 12h14" stroke="currentColor" stroke-width="1" opacity="0.6"/></svg>';
const PIT = '<svg viewBox="0 0 16 16" aria-hidden="true"><ellipse cx="6" cy="7.5" rx="4" ry="3" fill="currentColor"/><path d="M9.5 9.5l4.5 4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';
/** A loose stone. */
const BOULDER = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 11.5l1-5 4-3 4 2 1.5 5-3 2.5H5z" fill="currentColor"/><path d="M1 14h14" stroke="currentColor" stroke-width="1.2"/></svg>';

type Mark = { el: HTMLButtonElement; state: HTMLElement; point: THREE.Vector3; at: number };

/**
 * The Long Hunt on screen (docs/sendas/barbaro.md, chapter 1), inside a path's own plate (game/senda-hud.ts) and over
 * the board; a phone's plate kept under about a quarter of the screen:
 *
 * - the broken corral (`pens`): the herd (a pip a boar; out of the corral, red), how many may still go (the line of
 *   eight), «¡Se lleva uno! 2 s» red while a wolf drags one to the wall, the Grey Wolf while it is in. Over the board, at
 *   the stone the dragger makes for, its seconds left.
 * - the tracks (`tracks`, `brink`): the crossing of all and the rain's bar over it (its seconds; washed, «que huela
 *   Terrón»); the clues, in two short lines with the board's own marks (the pack's red, the Wolf's white); Terrón (to
 *   the crossing, sniffing, he showed it, down); on the ledge the howler (its next howl, rearing, howling: «¡cortalo!»).
 *   Over the board, a mark on the crossing at hand («Terrón»: a tap takes him there) and on each loose stone (a tap sends
 *   the hero to push it).
 *
 * Plain DOM, written only when it changes (docs/RENDIMIENTO.md); the board's points from the sim's own rules.
 */
export class CazaHud {
  readonly panel: HTMLElement;
  private kind: 'pens' | 'trail';
  private v = new THREE.Vector3();
  // The corral.
  private herdRow = h('div.bh-row.cz-herd');
  private herdPips: HTMLElement[] = [];
  private herdText = h('span.al-wave');
  private dragRow = h('div.bh-row.al-waverow.cz-drag');
  private dragText = h('span.al-wave');
  private chips: HTMLElement[] = [];
  // The tracks.
  private crossRow = h('div.bh-row.cz-cross');
  private crossText = h('span.al-wave');
  private rainFill = h('i');
  private rainText = h('small.cz-rain-s');
  private clueRow = h('div.bh-row.cz-clues');
  private terronRow = h('div.bh-row.al-waverow.cz-terron');
  private terronText = h('span.al-wave');
  private howlRow = h('div.bh-row.al-waverow.cz-howl');
  private howlText = h('span.al-wave');
  private marks: Mark[] = [];

  constructor(
    s: BattleState,
    private board: HTMLElement,
    private mark: (at: number) => void,
  ) {
    const sr = s.rules?.senda;
    this.kind = sr?.pens ? 'pens' : 'trail';
    const rows: HTMLElement[] = [];
    if (sr?.pens) {
      this.herdPips = sr.pens.homes.map(() => h('i.on'));
      this.herdRow.append(h('span.bh-ico', { html: BOAR }), h('span.bh-label', {}, t('senda.caza.herd')), h('span.al-pips.cz-pips', { title: t('senda.caza.herdHint', { n: sr.pens.keep }) }, ...this.herdPips), this.herdText);
      this.dragRow.append(h('span.bh-ico', { html: PAW }), this.dragText);
      rows.push(this.herdRow, this.dragRow);
      for (let i = 0; i < 6; i++) {
        const c = h('div.cz-chip');
        c.hidden = true;
        this.board.append(c);
        this.chips.push(c);
      }
    } else if (sr?.trail) {
      const r = sr.trail;
      this.crossRow.append(h('span.bh-ico', { html: RAIN }), this.crossText, h('span.bar.cz-rain', {}, this.rainFill), this.rainText);
      this.clueRow.append(
        h('span.cz-clue.pack', {}, h('span.cz-ci', { html: JUMP }), t('senda.caza.clueJump')),
        h('span.cz-clue.pack', {}, h('span.cz-ci', { html: DRINK }), t('senda.caza.clueDrink')),
        h('span.cz-clue.wolf', {}, h('span.cz-ci', { html: STONES }), t('senda.caza.clueStones')),
        h('span.cz-clue.wolf', {}, h('span.cz-ci', { html: PIT }), t('senda.caza.cluePit')),
      );
      this.terronRow.append(h('span.bh-ico', { html: BOAR }), h('span.bh-label', {}, t('senda.caza.terron')), this.terronText);
      rows.push(this.crossRow, this.clueRow, this.terronRow);
      if (r.howler) {
        this.howlRow.append(h('span.bh-ico', { html: HOWL }), h('span.bh-label', {}, t('senda.caza.howler')), this.howlText);
        rows.push(this.howlRow);
      }
      this.buildMarks(r.stones ?? []);
    }
    this.panel = h(`div.bh.cz-panel.cz-${this.kind}`, {}, ...rows);
  }

  /** The crossing's mark (Terrón) and the loose stones'. */
  private buildMarks(stones: { x: number; y: number }[]): void {
    const make = (at: number, icon: string, name: string, hint: string): Mark => {
      const state = h('small.al-state.cz-state');
      const el = h(
        `button.al-stone.cz-mark${at === 0 ? '.terron' : '.stone'}`,
        { type: 'button', 'data-test': at === 0 ? 'caza-terron' : `caza-stone-${at - 1}`, 'aria-label': hint, title: hint, onpointerup: (e: Event) => (e.stopPropagation(), this.mark(at)) },
        h('span.al-ring'),
        h('span.al-glyph', { html: icon }),
        h('b.mn-name.cz-name', {}, name),
        state,
      ) as HTMLButtonElement;
      this.board.append(el);
      return { el, state, point: new THREE.Vector3(), at };
    };
    this.marks.push(make(0, BOAR, t('senda.caza.terron'), t('senda.caza.terronHint')));
    stones.forEach((p, k) => {
      const m = make(k + 1, BOULDER, t('senda.caza.stone'), t('senda.caza.stoneHint'));
      simToWorld(p.x, p.y, m.point, 0.55);
      this.marks.push(m);
    });
  }

  private updatePens(s: BattleState): void {
    const r = s.rules?.senda?.pens;
    const w = s.senda?.pens;
    if (!r || !w) return;
    const kept = pensKept(w);
    w.boars.forEach((b, i) => this.herdPips[i]?.classList.toggle('on', !b.lost));
    const ht = t('senda.caza.kept', { n: kept, keep: r.keep });
    if (this.herdText.textContent !== ht) this.herdText.textContent = ht;
    this.herdRow.classList.toggle('low', kept <= r.keep + 1);
    // The most urgent drag: the one nearest its wall.
    let left = -1;
    for (const p of w.pack) if (p.until > 0 && (left < 0 || p.until - s.tick < left)) left = Math.max(0, p.until - s.tick);
    const gray = w.gray.phase >= 1 && w.gray.phase < 4;
    const text = left >= 0 ? t('senda.caza.drag', { s: Math.max(1, Math.ceil(left / TICK_HZ)) }) : gray ? t('senda.caza.gray') : w.done >= 0 ? t('senda.caza.gone') : t('senda.caza.calm');
    if (this.dragText.textContent !== text) this.dragText.textContent = text;
    this.dragRow.classList.toggle('warn', left >= 0);
    this.dragRow.classList.toggle('gray', gray && left < 0);
  }

  private updateTrail(s: BattleState): void {
    const r = s.rules?.senda?.trail;
    const w = s.senda?.trail;
    if (!r || !w) return;
    const n = r.crossings.length;
    const at = Math.min(n - 1, w.at);
    const ct = w.done >= 0 ? t('senda.caza.end') : t('senda.caza.cross', { n: at + 1, of: n });
    if (this.crossText.textContent !== ct) this.crossText.textContent = ct;
    // The rain over the crossing at hand: from the last washed (or the start) to this one's.
    const washed = trailWashed(s, r, at);
    const from = at > 0 ? (r.wash[at - 1] ?? 0) : 0;
    const to = r.wash[at] ?? 1;
    const k = washed || w.done >= 0 ? 0 : Math.max(0, Math.min(1, (to - s.waveT) / Math.max(1, to - from)));
    setStyle(this.rainFill, 'width', `${(k * 100).toFixed(1)}%`);
    const rs = w.done >= 0 ? '' : washed ? t('senda.caza.washed') : t('senda.caza.rain', { s: Math.max(0, Math.ceil((to - s.waveT) / TICK_HZ)) });
    if (this.rainText.textContent !== rs) this.rainText.textContent = rs;
    this.crossRow.classList.toggle('washed', washed && w.done < 0);
    this.crossRow.classList.toggle('soon', !washed && w.done < 0 && to - s.waveT < 8 * TICK_HZ);
    // Terrón.
    const tn = s.senda?.npcs.find((x) => x.id === 'terron');
    const tt = !tn || tn.hp <= 0 ? t('senda.caza.terronDown') : w.shown.includes(w.at) ? t('senda.caza.terronShown') : w.sniff >= 0 ? t('senda.caza.terronSniff', { s: Math.max(1, Math.ceil((w.sniff + r.sniff - s.tick) / TICK_HZ)) }) : w.job === 0 ? t('senda.caza.terronGo') : r.kind === 'brink' ? t('senda.caza.terronCost') : t('senda.caza.terronFree');
    if (this.terronText.textContent !== tt) this.terronText.textContent = tt;
    this.terronRow.classList.toggle('warn', washed && !w.shown.includes(w.at) && w.done < 0);
    // The howler.
    const st = s.senda?.howler;
    if (r.howler && st) {
      const ht = st.phase === 3 ? t('senda.caza.howlerDown') : st.phase === 1 ? t('senda.caza.rear', { s: Math.max(1, Math.ceil((st.until - s.tick + r.howler.howl) / TICK_HZ)) }) : st.phase === 2 ? t('senda.caza.howl') : t('senda.caza.howlIn', { s: Math.max(0, Math.ceil((st.next - s.waveT) / TICK_HZ)) }) + (st.cuts || st.howls ? ` · ${t('senda.caza.howls', { c: st.cuts, h: st.howls })}` : '');
      if (this.howlText.textContent !== ht) this.howlText.textContent = ht;
      this.howlRow.classList.toggle('warn', st.phase === 1 || st.phase === 2);
    }
    // The marks: Terrón's on the crossing at hand while he can help; the stones still lying.
    const c = r.crossings[w.at];
    for (const m of this.marks) {
      let gone = w.done >= 0 || s.result !== 'running';
      if (m.at === 0) {
        gone = gone || !c || !tn || tn.hp <= 0 || w.shown.includes(w.at);
        if (c) simToWorld(c.at.x, c.at.y, m.point, 0.3);
        const st2 = w.sniff >= 0 ? t('senda.caza.sniffMark') : '';
        if (m.state.textContent !== st2) {
          m.state.textContent = st2;
          remeasureMark(m.el);
        }
        m.el.classList.toggle('sent', w.job === 0 || w.sniff >= 0);
        m.el.style.setProperty('--k', w.sniff >= 0 ? Math.min(1, (s.tick - w.sniff) / Math.max(1, r.sniff)).toFixed(2) : '0');
      } else {
        const stn = w.stones[m.at - 1];
        gone = gone || !stn || stn.mode !== 0;
        m.el.classList.toggle('sent', w.job === m.at);
        m.el.style.setProperty('--k', w.job === m.at && r.stone ? Math.min(1, w.held / Math.max(1, r.stone.stand)).toFixed(2) : '0');
        // Near the howler while it rears: the stone's mark lights.
        const hs = s.senda?.howler;
        m.el.classList.toggle('hot', !!hs && (hs.phase === 1 || hs.phase === 2));
      }
      m.el.classList.toggle('gone', gone);
      m.el.disabled = gone;
    }
  }

  update(s: BattleState): void {
    if (this.kind === 'pens') this.updatePens(s);
    else this.updateTrail(s);
  }

  /** Every frame: the marks and chips over their places. */
  place(s: BattleState | undefined, camera: THREE.Camera, w: number, ht: number): void {
    for (const m of this.marks) {
      if (m.el.classList.contains('gone')) {
        setStyle(m.el, 'display', 'none');
        continue;
      }
      this.v.copy(m.point).project(camera);
      if (this.v.z > 1 || this.v.x < -1.1 || this.v.x > 1.1 || this.v.y < -1.1 || this.v.y > 1.1) {
        setStyle(m.el, 'display', 'none');
        continue;
      }
      setStyle(m.el, 'display', '');
      pinMark(m.el, Math.min(w - 40, Math.max(40, (this.v.x * 0.5 + 0.5) * w)), (-this.v.y * 0.5 + 0.5) * ht);
    }
    // The corral: the seconds left at each stone a wolf drags a boar to.
    const ws = s?.senda?.pens;
    let k = 0;
    for (const p of ws?.pack ?? []) {
      if (p.until <= 0 || k >= this.chips.length) continue;
      const c = this.chips[k++] as HTMLElement;
      simToWorld(p.exit.x, p.exit.y, this.v, 0.4).project(camera);
      const txt = `${Math.max(1, Math.ceil((p.until - (s?.tick ?? 0)) / TICK_HZ))} s`;
      if (c.textContent !== txt) c.textContent = txt;
      c.hidden = this.v.z > 1;
      pinMark(c, Math.min(w - 30, Math.max(30, (this.v.x * 0.5 + 0.5) * w)), (-this.v.y * 0.5 + 0.5) * ht - 22);
    }
    for (; k < this.chips.length; k++) (this.chips[k] as HTMLElement).hidden = true;
  }
}
